import React, { useState, useEffect, useRef } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function MultiUploadModal({ caseInfo, onClose, dbOps, currentUserProfile, storage }) {
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const processFiles = (files) => {
    const newFiles = Array.from(files).map(f => {
      const parts = f.webkitRelativePath ? f.webkitRelativePath.split('/') : [];
      const folderName = parts.length > 1 ? parts[0] : null;
      return {
        id: Math.random().toString(36).substr(2, 9),
        file: f,
        name: f.name,
        folder: folderName,
        securityLevel: 'רגיל'
      };
    });
    setStagedFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleSaveAll = async () => {
    setIsUploading(true);
    const today = new Date().toISOString().split('T')[0];
    
    for (const sf of stagedFiles) {
      let downloadUrl = null;
      
      if (sf.file && storage) {
        try {
          const fileRef = storageRef(storage, `documents/${caseInfo.id}/${Date.now()}_${sf.file.name}`);
          await uploadBytes(fileRef, sf.file);
          downloadUrl = await getDownloadURL(fileRef);
        } catch (e) {
          console.error("Storage upload error", e);
        }
      }

      await dbOps.handleSave('documents', {
        name: sf.name,
        caseId: caseInfo.id,
        uploadedBy: currentUserProfile?.name || 'צוות המשרד',
        date: today,
        size: sf.file ? `${(sf.file.size / 1024 / 1024).toFixed(2)} MB` : '0 MB',
        securityLevel: sf.securityLevel,
        folder: sf.folder || null,
        fileUrl: downloadUrl
      });
    }
    
    setIsUploading(false);
    onClose();
  };

  const removeFile = (id) => {
    setStagedFiles(stagedFiles.filter(f => f.id !== id));
  };

  const updateFileName = (id, newName) => {
    setStagedFiles(stagedFiles.map(f => f.id === id ? { ...f, name: newName } : f));
  };

  const standaloneFiles = stagedFiles.filter(f => !f.folder);
  const folders = [...new Set(stagedFiles.filter(f => f.folder).map(f => f.folder))];

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 m-0" dir="rtl">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[95vh] animate-in fade-in zoom-in duration-200">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><UploadCloud className="w-5 h-5 text-blue-600" /> העלאת מסמכים ותיקיות</h3>
          <button onClick={onClose} disabled={isUploading} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
          <div 
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${isDragging ? 'border-blue-500 bg-blue-50 scale-[1.02]' : 'border-slate-300 hover:bg-slate-50'} ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <UploadCloud className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
            <h4 className="text-lg font-bold text-slate-700 mb-2">גרור קבצים לכאן</h4>
            <p className="text-sm text-slate-500 mb-6">או השתמש בכפתורים לבחירה מהמחשב</p>
            
            <div className="flex justify-center gap-4">
              <input type="file" multiple className="hidden" ref={fileInputRef} onChange={(e) => processFiles(e.target.files)} />
              <input type="file" multiple webkitdirectory="true" directory="true" className="hidden" ref={folderInputRef} onChange={(e) => processFiles(e.target.files)} />
              
              <button onClick={() => fileInputRef.current.click()} className="bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm">
                בחר קבצים
              </button>
              <button onClick={() => folderInputRef.current.click()} className="bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2">
                <Folder className="w-4 h-4" /> בחר תיקיה שלמה
              </button>
            </div>
          </div>

          {stagedFiles.length > 0 && (
            <div className={`space-y-4 ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
              <h4 className="font-bold text-slate-800">קבצים בהמתנה לשמירה ({stagedFiles.length})</h4>
              
              {folders.map(folder => (
                <div key={folder} className="border border-indigo-200 rounded-xl overflow-hidden">
                  <div className="bg-indigo-50/50 p-3 border-b border-indigo-100 flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-indigo-600" />
                    <span className="font-bold text-indigo-900">{folder}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {stagedFiles.filter(f => f.folder === folder).map(f => (
                      <div key={f.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white hover:bg-slate-50">
                        <FileText className="w-5 h-5 text-slate-400 hidden sm:block shrink-0" />
                        <input 
                          type="text" 
                          value={f.name} 
                          onChange={(e) => updateFileName(f.id, e.target.value)}
                          className="flex-1 p-2 text-sm border border-slate-200 rounded focus:border-blue-500 outline-none"
                        />
                        <select 
                          value={f.securityLevel} 
                          onChange={(e) => {
                            setStagedFiles(stagedFiles.map(file => file.id === f.id ? { ...file, securityLevel: e.target.value } : file));
                          }}
                          className="p-2 text-sm border border-slate-200 rounded bg-white outline-none w-full sm:w-32"
                        >
                          <option value="רגיל">רגיל</option>
                          <option value="פנימי">פנימי</option>
                          <option value="סודי ביותר">סודי ביותר</option>
                        </select>
                        <button onClick={() => removeFile(f.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors self-end sm:self-auto"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {standaloneFiles.length > 0 && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-50 p-3 border-b border-slate-200 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <span className="font-bold text-slate-800">קבצים ללא תיקיה</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {standaloneFiles.map(f => (
                      <div key={f.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-white hover:bg-slate-50">
                        <FileText className="w-5 h-5 text-slate-400 hidden sm:block shrink-0" />
                        <input 
                          type="text" 
                          value={f.name} 
                          onChange={(e) => updateFileName(f.id, e.target.value)}
                          className="flex-1 p-2 text-sm border border-slate-200 rounded focus:border-blue-500 outline-none"
                        />
                        <select 
                          value={f.securityLevel} 
                          onChange={(e) => {
                            setStagedFiles(stagedFiles.map(file => file.id === f.id ? { ...file, securityLevel: e.target.value } : file));
                          }}
                          className="p-2 text-sm border border-slate-200 rounded bg-white outline-none w-full sm:w-32"
                        >
                          <option value="רגיל">רגיל</option>
                          <option value="פנימי">פנימי</option>
                          <option value="סודי ביותר">סודי ביותר</option>
                        </select>
                        <button onClick={() => removeFile(f.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors self-end sm:self-auto"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} disabled={isUploading} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-200 disabled:opacity-50 rounded-lg transition-colors">ביטול</button>
          <button 
            onClick={handleSaveAll} 
            disabled={stagedFiles.length === 0 || isUploading}
            className="px-6 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 rounded-lg transition-colors flex items-center gap-2 shadow-sm"
          >
            {isUploading ? <RefreshCw className="w-4 h-4 animate-spin"/> : <UploadCloud className="w-4 h-4"/>} 
            {isUploading ? 'מעלה קבצים...' : 'העלה ושמור לענן'}
          </button>
        </div>
      </div>
    </div>
  );
}
